import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const SettlementConfirmation = ({ reconciliation, onClose, onConfirm }) => {
  const { user } = useAuth();
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const handleConfirmSettlement = async () => {
    if (!signature?.trim()) {
      alert('Please enter your name as digital signature');
      return;
    }

    setLoading(true);
    try {
      // Update reconciliation with settlement confirmation
      const { error } = await supabase
        ?.from('cash_reconciliations')
        ?.update({
          status: 'approved',
          settlement_confirmed: true,
          settlement_signature: signature,
          settlement_notes: notes?.trim() || null,
          settlement_confirmed_by: user?.id,
          settlement_confirmed_at: new Date()?.toISOString()
        })
        ?.eq('id', reconciliation?.id);

      if (error) throw error;

      alert('Settlement confirmed successfully');
      onConfirm();
    } catch (error) {
      console.error('Settlement confirmation error:', error);
      alert('Failed to confirm settlement');
    } finally {
      setLoading(false);
    }
  };

  const variance = reconciliation?.actual_balance - reconciliation?.expected_balance;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full p-6 border border-border my-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={28} className="text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Settlement Confirmation</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Settlement Report */}
        <div className="space-y-4 mb-6">
          {/* Drawer Info */}
          <div className="p-4 bg-background rounded-lg border border-border">
            <h3 className="font-bold text-foreground mb-3">Drawer Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Drawer Name</p>
                <p className="font-medium text-foreground">{reconciliation?.cash_drawers?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Reconciled By</p>
                <p className="font-medium text-foreground">{reconciliation?.user_profiles?.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Date</p>
                <p className="font-medium text-foreground">{new Date(reconciliation?.created_at)?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Time</p>
                <p className="font-medium text-foreground">{new Date(reconciliation?.created_at)?.toLocaleTimeString()}</p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="p-4 bg-background rounded-lg border border-border">
            <h3 className="font-bold text-foreground mb-3">Financial Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Expected Balance:</span>
                <span className="font-bold text-foreground">${reconciliation?.expected_balance?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Actual Balance:</span>
                <span className="font-bold text-primary">${reconciliation?.actual_balance?.toFixed(2)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between items-center">
                <span className="font-medium text-foreground">Variance:</span>
                <span className={`font-bold text-lg ${
                  variance === 0 ? 'text-green-500' : 
                  Math.abs(variance) <= 5 ? 'text-orange-500' : 'text-red-500'
                }`}>
                  {variance >= 0 ? '+' : ''}${variance?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Variance Reason */}
          {reconciliation?.variance_reason && (
            <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <h3 className="font-bold text-foreground mb-2">Variance Explanation</h3>
              <p className="text-sm text-muted-foreground">{reconciliation?.variance_reason}</p>
            </div>
          )}

          {/* Denomination Breakdown */}
          {reconciliation?.cash_denominations && reconciliation?.cash_denominations?.length > 0 && (
            <div className="p-4 bg-background rounded-lg border border-border">
              <h3 className="font-bold text-foreground mb-3">Denomination Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {reconciliation?.cash_denominations?.map((denom, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="text-muted-foreground">
                      ${denom?.denomination_value?.toFixed(2)} × {denom?.quantity}
                    </span>
                    <span className="font-medium text-foreground ml-2">
                      = ${(denom?.denomination_value * denom?.quantity)?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settlement Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Settlement Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Add any additional notes about this settlement..."
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
            />
          </div>

          {/* Digital Signature */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Digital Signature <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e?.target?.value)}
              placeholder="Type your full name to confirm"
              className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
            <p className="text-xs text-muted-foreground mt-1">
              By typing your name, you confirm the accuracy of this settlement report
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleConfirmSettlement}
            disabled={loading || !signature?.trim()}
            iconName="CheckCircle"
            iconPosition="left"
            fullWidth
          >
            {loading ? 'Confirming...' : 'Confirm Settlement'}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettlementConfirmation;